import React, { useEffect, useState } from 'react';
import { Collapse, Popover, Progress, Tabs } from 'antd';
import {
    ArrowsClockwise,
    Check,
    Clock,
    FileText,
    Flag,
    FloppyDisk,
    ClockCounterClockwise,
    House,
    List,
    ListChecks,
    PaperPlaneTilt,
    PencilSimple,
    Sun,
    BatteryFull,
    Recycle,
    Wrench,
    Warning,
    WarningCircle,
    LockOpen,
    X,
} from '@phosphor-icons/react';
import type { SalesRepChecklistEngine } from './salesrepChecklistEngine';
import type { QuestionAnswerHistoryRecord } from './salesrepAuditTrail';
import type { ProcessedAuditTrailData } from './salesrepAuditTrail';
import { QuestionControl } from './QuestionControl';
import { SalesRepChecklistSummary } from './SalesRepChecklistSummary';

type SalesRepChecklistPanelProps = {
    engine: SalesRepChecklistEngine;
    tick: number;
    auditTrail: ProcessedAuditTrailData;
    salesForceId?: string | null;
    onSave: () => void;
};

function productTabIcon(product: string) {
    const props = { size: 14, weight: 'duotone' as const };
    switch (product) {
        case 'Shared':
            return <List {...props} />;
        case 'Solar':
            return <Sun {...props} />;
        case 'Roof':
            return <House {...props} />;
        case 'Battery':
            return <BatteryFull {...props} />;
        case 'RRR':
            return <Recycle {...props} />;
        case 'RR':
        case 'R&R':
            return <Wrench {...props} />;
        default:
            return <ListChecks {...props} />;
    }
}

function QuestionList({
    questions,
    level,
    engine,
    historyMap,
    openHistoryPopoverId,
    onOpenHistoryPopoverChange,
}: {
    questions: any[];
    level: number;
    engine: SalesRepChecklistEngine;
    historyMap: Map<number, QuestionAnswerHistoryRecord[]>;
    openHistoryPopoverId: number | string | null;
    onOpenHistoryPopoverChange: (id: number | string | null) => void;
}) {
    const sorted = engine.getSortedQuestions(questions);
    return (
        <>
            {sorted.map((q) => {
                const history = historyMap.get(Number(q.questionId)) || [];
                const tone = engine.getQuestionSelectedTone(q);
                const toneColor = engine.getQuestionSelectedToneColor(q);
                const answered = engine.isQuestionAnswered(q);
                const isTopLevel = level === 0 && (!q.parentQuestionId || q.parentQuestionId === 0);
                const children = engine.getActiveChildQuestions(q);
                return (
                    <React.Fragment key={q.questionId ?? q.displayOrder}>
                        <div
                            id={`question-${q.questionId}`}
                            className={[
                                'obd-src-question-card',
                                answered ? 'is-answered' : 'is-unanswered',
                                level > 0 ? 'is-child' : '',
                                tone ? `is-tone-${tone}` : '',
                            ]
                                .filter(Boolean)
                                .join(' ')}
                            style={
                                {
                                    borderColor: toneColor || undefined,
                                    ['--card-tone-color' as string]: toneColor || undefined,
                                } as React.CSSProperties
                            }
                        >
                            <div className="obd-src-question-card__label">
                                <div className="obd-src-question-card__title">
                                    <span className={`obd-src-q-badge ${isTopLevel ? 'is-orange' : 'is-blue'}`}>
                                        {q.displayOrder}
                                    </span>
                                    <span className="obd-src-question-card__text">{q.text || q?.questionText}</span>
                                    {q?.isPrefilled && (
                                        <>
                                            <span className="obd-src-muted">(from opportunity)</span>
                                            <span className="obd-src-tag">Opportunity</span>
                                            <span className="obd-src-tag">Pre-filled</span>
                                        </>
                                    )}
                                    {!q?.isPrefilled && engine.isQuestionRequired(q) && (
                                        <span className="obd-src-tag obd-src-tag--required">required</span>
                                    )}
                                </div>
                                <div className="obd-src-question-card__meta">
                                    {engine.isQuestionUnsaved(q) && (
                                        <span className="obd-src-unsaved">
                                            <Clock size={12} /> Unsaved
                                        </span>
                                    )}
                                    {history.length > 0 && (
                                        <Popover
                                            open={openHistoryPopoverId === q.questionId}
                                            onOpenChange={(newOpen) =>
                                                onOpenHistoryPopoverChange(newOpen ? q.questionId : null)
                                            }
                                            trigger="click"
                                            placement="bottomRight"
                                            title={
                                                <span className="obd-src-history-title">
                                                    <ClockCounterClockwise size={14} /> Answer History
                                                </span>
                                            }
                                            content={
                                                <div className="obd-src-history-popover">
                                                    {history.map((h, idx) => (
                                                        <div key={`${h.historyId}-${idx}`} className="obd-src-history-item">
                                                            <div className="obd-src-history-item__row">
                                                                <span className="obd-src-avatar obd-src-avatar--sm">
                                                                    {h.modifiedUserInitials}
                                                                </span>
                                                                <span className="obd-src-history-item__name">
                                                                    {h.modifiedUserFullName}
                                                                </span>
                                                                <span className={`obd-src-tone-pill is-${h.answerTone}`}>
                                                                    {h.answerText}
                                                                </span>
                                                            </div>
                                                            <div className="obd-src-muted obd-src-history-item__time">
                                                                <Clock size={11} /> {h.formattedTime}
                                                            </div>
                                                        </div>
                                                    ))}
                                                </div>
                                            }
                                        >
                                            <button type="button" className="obd-src-history-chip">
                                                <ClockCounterClockwise size={12} /> History
                                                <span className="obd-src-history-chip__count">{history.length}</span>
                                            </button>
                                        </Popover>
                                    )}
                                </div>
                            </div>
                            <QuestionControl
                                question={q}
                                onResponseChange={(question) => engine.onQuestionResponseChange(question)}
                            />
                        </div>
                        {children.length > 0 && (
                            <QuestionList
                                questions={children}
                                level={level + 1}
                                engine={engine}
                                historyMap={historyMap}
                                openHistoryPopoverId={openHistoryPopoverId}
                                onOpenHistoryPopoverChange={onOpenHistoryPopoverChange}
                            />
                        )}
                    </React.Fragment>
                );
            })}
        </>
    );
}

function StageRow({
    name,
    icon,
    stats,
    track,
    complete,
}: {
    name: string;
    icon: React.ReactNode;
    stats: { answered: number; total: number; percentage: number };
    track: ReturnType<SalesRepChecklistEngine['getChecklistTrackStatus']>;
    complete: boolean;
}) {
    return (
        <div className="obd-src-stage">
            <span className="obd-src-stage__name">
                {icon} {name}
            </span>
            <div className="obd-src-stage__bar">
                <Progress
                    percent={stats.percentage}
                    showInfo={false}
                    size={8}
                    strokeColor={track.color}
                    trailColor="#e2e8f0"
                />
                <span className="obd-src-stage__count">
                    {stats.answered} of {stats.total}
                </span>
                {track.flaggedCount > 0 && (
                    <span className="obd-src-flag-badge" style={{ backgroundColor: track.color }}>
                        <Flag size={11} /> {track.flaggedCount} {track.bannerType === 'amber' ? 'to review' : 'flagged'}
                    </span>
                )}
                {track.flaggedCount === 0 && (
                    <span className={`obd-src-status-badge ${complete ? 'is-success' : 'is-progress'}`}>
                        {complete ? 'COMPLETED' : 'IN PROGRESS'}
                    </span>
                )}
            </div>
        </div>
    );
}

function EmptyState({ title, body }: { title: string; body: string }) {
    return (
        <div className="obd-src-empty">
            <ListChecks size={42} weight="duotone" />
            <h3>{title}</h3>
            <p>{body}</p>
        </div>
    );
}

function ChecklistBody({
    engine,
    tick,
    checklistName,
    historyMap,
    showProductSelector,
    openHistoryPopoverId,
    onOpenHistoryPopoverChange,
}: {
    engine: SalesRepChecklistEngine;
    tick: number;
    checklistName: string;
    historyMap: Map<number, QuestionAnswerHistoryRecord[]>;
    showProductSelector: boolean;
    openHistoryPopoverId: number | string | null;
    onOpenHistoryPopoverChange: (id: number | string | null) => void;
}) {
    void tick;
    const q0 = showProductSelector ? engine.getSectionZeroQuestion(checklistName) : null;
    const productTabs =
        checklistName === 'Contract Submission'
            ? engine.getChecklistSubTabs(checklistName)
            : engine.getActiveProducts();
    const sections = engine.checklistDynamicTemplates[engine.activeProductTab] || [];
    const answered = engine.getProductAnsweredCount(engine.activeProductTab);
    const total = engine.getProductTotalCount(engine.activeProductTab);
    const overall = engine.getOverallTrackStatus();
    const pct = total > 0 ? (answered / total) * 100 : 0;

    return (
        <>
            {q0 && (
                <div className="obd-src-product-selector">
                    <span className="obd-src-product-selector__label">{q0?.questionText}</span>
                    <QuestionControl
                        question={q0}
                        selectedProducts={engine.selectedProducts}
                        isProductSelector
                        onProductToggle={(product) => engine.toggleProduct(product, checklistName)}
                    />
                    {engine.productSelectionValidationMessage && (
                        <div className="obd-src-product-validation">{engine.productSelectionValidationMessage}</div>
                    )}
                </div>
            )}

            {!engine.isAnyProductSelected() ? (
                <EmptyState
                    title="Select a product to begin"
                    body={
                        showProductSelector
                            ? 'Choose Solar, Roof, Battery, RRR, or RR above. Multiple products can be selected — each gets its own checklist tab.'
                            : 'Choose products in Contract Creation to show the matching Contract Submission checklist.'
                    }
                />
            ) : (
                <>
                    <div className="obd-src-product-subtabs">
                        {productTabs.map((prod) => (
                            <button
                                key={prod}
                                type="button"
                                className={`obd-src-product-subtab ${engine.activeProductTab === prod ? 'is-active' : ''}`}
                                onClick={() => engine.selectProductTab(prod)}
                            >
                                {productTabIcon(prod)}
                                {engine.getProductAnsweredCount(prod) > 0 && <span className="obd-src-dot" />}
                                {prod}
                            </button>
                        ))}
                    </div>
                    <div className="obd-src-product-progress">
                        <span className="obd-src-muted">
                            {answered} of {total} answered
                        </span>
                        <div className="obd-src-product-progress__bar">
                            <Progress percent={pct} showInfo={false} size={8} strokeColor={overall.color} trailColor="#e2e8f0" />
                        </div>
                    </div>
                    <Collapse
                        accordion={false}
                        expandIconPosition="end"
                        className="obd-src-collapse"
                        activeKey={sections
                            .filter((section: any) => section.expanded)
                            .map((section: any) => String(section.sectionId ?? section.sectionName))}
                        onChange={(keys) => {
                            const open = new Set((Array.isArray(keys) ? keys : [keys]).map(String));
                            sections.forEach((section: any) => {
                                const id = String(section.sectionId ?? section.sectionName);
                                const wantOpen = open.has(id);
                                if (wantOpen !== !!section.expanded) {
                                    engine.onSectionHeaderToggle(section, wantOpen);
                                }
                            });
                        }}
                        items={sections.map((section: any) => {
                            const tone = engine.getSectionTone(section);
                            const toneColor = engine.getSectionToneColor(section);
                            const idPrefix = checklistName === 'Contract Submission' ? 'submission-section-panel-' : 'section-panel-';
                            return {
                                key: String(section.sectionId ?? section.sectionName),
                                id: `${idPrefix}${section.sectionId || section.sectionName}`,
                                className: `obd-src-panel obd-src-panel--${tone}`,
                                style: { ['--panel-tone-color' as string]: toneColor || undefined } as React.CSSProperties,
                                label: (
                                    <div className="obd-src-panel-head">
                                        <div className="obd-src-panel-head__left">
                                            <span className="obd-src-panel-head__title">
                                                {section.sectionName || section.title}
                                            </span>
                                            {engine.shouldShowCriticalTag(section) && (
                                                <span className="obd-src-tag">Critical</span>
                                            )}
                                        </div>
                                        <div className="obd-src-panel-head__right">
                                            {tone === 'red' && (
                                                <span className="obd-src-section-chip" style={{ backgroundColor: toneColor || undefined }}>
                                                    <Warning size={12} /> Needs attention
                                                </span>
                                            )}
                                            {tone === 'amber' && (
                                                <span className="obd-src-section-chip" style={{ backgroundColor: toneColor || undefined }}>
                                                    <Warning size={12} /> Review required
                                                </span>
                                            )}
                                            {tone !== 'red' && tone !== 'amber' && (
                                                <>
                                                    <span className="obd-src-answered-text">
                                                        {engine.getSectionAnsweredCount(section, engine.activeProductTab)} / {engine.getSectionTotalCount(section, engine.activeProductTab)} answered
                                                    </span>
                                                    {engine.isSectionComplete(section, engine.activeProductTab) && (
                                                        <span className="obd-src-complete-check">
                                                            <Check size={11} weight="bold" />
                                                        </span>
                                                    )}
                                                </>
                                            )}
                                        </div>
                                    </div>
                                ),
                                children: (
                                    <div className="obd-src-questions">
                                        <QuestionList
                                            questions={section.questions || section.questionsList || []}
                                            level={0}
                                            engine={engine}
                                            historyMap={historyMap}
                                            openHistoryPopoverId={openHistoryPopoverId}
                                            onOpenHistoryPopoverChange={onOpenHistoryPopoverChange}
                                        />
                                    </div>
                                ),
                            };
                        })}
                    />
                </>
            )}
        </>
    );
}

export const SalesRepChecklistPanel: React.FC<SalesRepChecklistPanelProps> = ({
    engine,
    tick,
    auditTrail,
    salesForceId,
    onSave,
}) => {
    void tick;
    const [openHistoryPopoverId, setOpenHistoryPopoverId] = useState<number | string | null>(null);

    useEffect(() => {
        const handleScroll = (e: Event) => {
            const target = e.target as HTMLElement | null;
            if (target && target.closest && target.closest('.obd-src-history-popover')) {
                return;
            }
            setOpenHistoryPopoverId(null);
        };
        window.addEventListener('scroll', handleScroll, true);
        return () => {
            window.removeEventListener('scroll', handleScroll, true);
        };
    }, []);
    const overall = engine.getOverallTrackStatus();
    const overallStats = engine.getOverallStats();
    const creationStats = engine.getChecklistCompletionStats('Contract Creation');
    const submissionStats = engine.getChecklistCompletionStats('Contract Submission');
    const creationTrack = engine.getChecklistTrackStatus('Contract Creation');
    const submissionTrack = engine.getChecklistTrackStatus('Contract Submission');
    const opportunityLabel =
        salesForceId ||
        engine.tobarDetails?.salesForceId ||
        engine.opportunityId ||
        'Unknown';

    const tabItems = [
        ...(engine.salesrepChecklistDetails || []).map((checklist: any, index: number) => ({
            key: String(index),
            label: (
                <span className="obd-src-tab-label">
                    {checklist.checklistName === 'Contract Creation' ? <PencilSimple size={14} /> : <PaperPlaneTilt size={14} />}
                    {checklist.checklistName}
                    <span
                        className={`obd-src-tab-pill ${engine.getChecklistTabPillClass(checklist.checklistName)}`}
                        style={
                            engine.getChecklistTrackStatus(checklist.checklistName).track !== 'A'
                                ? {
                                      backgroundColor: `${engine.getChecklistTrackStatus(checklist.checklistName).color}20`,
                                      color: engine.getChecklistTrackStatus(checklist.checklistName).color,
                                  }
                                : undefined
                        }
                    >
                        {engine.getChecklistTabStatusLabel(checklist.checklistName)}
                    </span>
                </span>
            ),
        })),
        {
            key: String((engine.salesrepChecklistDetails || []).length),
            label: (
                <span className="obd-src-tab-label">
                    <List size={14} />
                    Summary
                    <span
                        className={`obd-src-tab-pill tab-pill-summary ${
                            overall.track === 'C' ? 'tab-pill-danger' : overall.track === 'B' ? 'tab-pill-warning' : ''
                        }`}
                        style={
                            overall.track !== 'A'
                                ? { backgroundColor: `${overall.color}20`, color: overall.color }
                                : undefined
                        }
                    >
                        {overall.track !== 'A' ? `Track ${overall.track}` : `${overallStats.percentage}%`}
                    </span>
                </span>
            ),
        },
    ];

    return (
        <div className="obd-salesrep-checklist">
            <div className="obd-src-topbar">
                <div className="obd-src-chip">
                    <span className="obd-src-chip__title">State</span>
                    <span className="obd-src-chip__value">{engine.tobarDetails?.state || '—'}</span>
                </div>
                <div className="obd-src-chip">
                    <span className="obd-src-chip__title">Utility</span>
                    <span className="obd-src-chip__value">{engine.tobarDetails?.utilityCompany || '—'}</span>
                </div>
                <div className="obd-src-chip obd-src-chip--opportunity">
                    <span className="obd-src-chip__title">Opportunity</span>
                    <span className="obd-src-chip__value">{opportunityLabel}</span>
                </div>
            </div>

            <div className="obd-src-tracker">
                <div
                    className="obd-src-track-box"
                    style={{
                        borderColor: overall.color,
                        backgroundColor: `${overall.color}15`,
                    }}
                >
                    <div className="obd-src-track-box__title" style={{ color: overall.color }}>
                        TRACK
                    </div>
                    <div className="obd-src-track-box__letter" style={{ color: overall.color }}>
                        {overall.track}
                    </div>
                    <div className="obd-src-track-box__sub" style={{ color: overall.color }}>
                        PROJECTED
                    </div>
                </div>
                <div className="obd-src-pct">
                    <div className="obd-src-pct__num">{overallStats.percentage}%</div>
                    <div className="obd-src-pct__lbl">OVERALL</div>
                    <div className="obd-src-pct__frac">
                        {overallStats.answered} of {overallStats.total}
                    </div>
                </div>
                <div className="obd-src-stages">
                    <StageRow
                        name="Contract Creation"
                        icon={<PencilSimple size={14} />}
                        stats={creationStats}
                        track={creationTrack}
                        complete={engine.isContractCreationComplete()}
                    />
                    <StageRow
                        name="Contract Submission"
                        icon={<PaperPlaneTilt size={14} />}
                        stats={submissionStats}
                        track={submissionTrack}
                        complete={engine.isChecklistComplete('Contract Submission')}
                    />
                </div>
            </div>

            {overall.bannerMessage && (
                <div
                    className={`obd-src-banner ${overall.bannerType === 'red' ? 'is-red' : 'is-amber'}`}
                    style={{ borderColor: overall.color, backgroundColor: `${overall.color}15` }}
                >
                    {overall.bannerType === 'red' ? <WarningCircle size={16} color={overall.color} /> : <Warning size={16} color={overall.color} />}
                    <span style={{ color: overall.color }}>{overall.bannerMessage}</span>
                </div>
            )}

            {engine.salesrepChecklistDetails.length > 0 && (
                <Tabs
                    activeKey={String(engine.selectedTabIndex)}
                    onChange={(key) => engine.selectTab(Number(key))}
                    items={tabItems}
                    className="obd-src-tabs"
                />
            )}

            {engine.isActiveChecklist('Contract Creation') && (
                <ChecklistBody
                    engine={engine}
                    tick={tick}
                    checklistName="Contract Creation"
                    historyMap={auditTrail.questionHistoryMap}
                    showProductSelector
                    openHistoryPopoverId={openHistoryPopoverId}
                    onOpenHistoryPopoverChange={setOpenHistoryPopoverId}
                />
            )}
            {engine.isActiveChecklist('Contract Submission') && (
                <ChecklistBody
                    engine={engine}
                    tick={tick}
                    checklistName="Contract Submission"
                    historyMap={auditTrail.questionHistoryMap}
                    showProductSelector={false}
                    openHistoryPopoverId={openHistoryPopoverId}
                    onOpenHistoryPopoverChange={setOpenHistoryPopoverId}
                />
            )}
            {engine.activeTab === 'summary' && (
                <SalesRepChecklistSummary
                    engine={engine}
                    tick={tick}
                    auditTrail={auditTrail}
                    onNavigateToQuestion={(item) => engine.onNavigateToQuestion(item)}
                />
            )}

            {engine.showReopenModal && (
                <div className="obd-src-reopen-overlay" onClick={() => engine.cancelReopenSection()}>
                    <div className="obd-src-reopen-card" onClick={(e) => e.stopPropagation()}>
                        <div className="obd-src-reopen-card__head">
                            <div>
                                <Warning size={18} /> Section Already Completed
                            </div>
                            <div className="obd-src-reopen-card__sub">This section has been marked as done</div>
                        </div>
                        <div className="obd-src-reopen-card__body">
                            <div className="obd-src-reopen-code">
                                <FileText size={16} />
                                {engine.sectionToReopen?.sectionName || engine.sectionToReopen?.title || 'Section'}
                            </div>
                            <p>This section is complete. Are you sure you want to reopen it and make changes?</p>
                            <div className="obd-src-reopen-warn">
                                <House size={16} />
                                Reopening will allow answers to be changed. All existing audit trail entries will be preserved.
                            </div>
                            <div className="obd-src-reopen-actions">
                                <button type="button" className="obd-src-btn-secondary" onClick={() => engine.cancelReopenSection()}>
                                    <X size={14} /> Keep Closed
                                </button>
                                <button type="button" className="obd-src-btn-reopen" onClick={() => engine.confirmReopenSection()}>
                                    <LockOpen size={14} /> Yes, Reopen
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            <div className={`obd-src-footer ${engine.unsavedChangesCount > 0 ? 'has-unsaved' : ''}`}>
                {engine.unsavedChangesCount > 0 && (
                    <span className="obd-src-unsaved-count">{engine.unsavedChangesCount} unsaved</span>
                )}
                <button
                    type="button"
                    className="obd-src-save-btn"
                    disabled={engine.saveDisabled}
                    onClick={onSave}
                    id="save-btn"
                >
                    <FloppyDisk size={16} />
                    SAVE
                </button>
            </div>
        </div>
    );
};
